import { fetchYearlyReports, downloadYearlyReport } from "@api/yearlyReports";
import { useEffect, useState } from "react";
import { useKeycloak } from "@react-keycloak/web";
import { reports } from "@constants/yearly-reports-data";

export type ReportType = {
  id: number;
  name: string;
  nameBn: string;
  fetchUrl: string;
  downloadUrl: string;
  column: Array<{
    id: number;
    name: string;
    key: string;
    render: (value: any) => JSX.Element;
  }>;
};

const useYearlyReports = () => {
  const [reportData, setReportData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [query, setQuery] = useState<any>({
    date: new Date().getFullYear(),
  });

  const [selectedReport, setSelectedReport] = useState<ReportType | undefined>(
    undefined,
  );
  const { keycloak } = useKeycloak();
  const token = keycloak.token as string;

  const handleQuery = (key: string, value: number | string) =>
    setQuery((prev: any) => ({
      ...prev,
      [key]: value,
    }));

  const fetchReport = async () => {
    if (selectedReport?.fetchUrl) {
      setIsLoading(true);
      setError(false);

      try {
        const response = await fetchYearlyReports(
          selectedReport?.fetchUrl,
          query,
        );
        if (response.status === "SUCCESS") {
          setReportData(response.resData?.data || []);
        } else {
          setError(true);
        }
      } catch (err) {
        setError(true);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const downloadReport = async (fileName: string, downloadUrl: string) => {
    if (downloadUrl) {
      setIsLoading(true);
      try {
        await downloadYearlyReport(token, fileName, query, downloadUrl);
      } catch (error) {
        alert("Error downloading file");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const selectedData = (id: number) => {
    const data = reports.find((report) => report.id === id);
    setSelectedReport(data);
  };

  // Refetch when query changes and we have a current URL
  useEffect(() => {
    if (query && selectedReport) {
      fetchReport();
    }
  }, [query, selectedReport]);

  return {
    reportData,
    isLoading,
    error,
    fetchReport,
    downloadReport,
    handleQuery,
    setQuery,
    selectedData,
    selectedReport,
    date: query.date,
  };
};

export default useYearlyReports;
